import React, { useCallback, useRef, useState } from "react"
import { saveAs } from "file-saver"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import {
  Copy,
  Download,
  Eye,
  ExternalLink,
  File,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Link2,
  Mail,
  type LucideIcon,
} from "lucide-react"
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { AIChatLink } from "./AIChatLink"

const URL_RE = /https?:\/\/[^\s<>"]+/g
/** Punctuation that usually ends the sentence, not the URL. */
const TRAILING_PUNCT_RE = /[.,;:!?)\]}'"]+$/

/** Extensions of web pages — a URL ending in one is a link, not a file. */
const PAGE_EXT_RE = /^(html?|php|aspx?|jsp)$/

type FileKind = { icon: LucideIcon; className?: string; preview?: boolean }

const FILE_KINDS: Array<[RegExp, FileKind]> = [
  [
    /^pdf$/,
    {
      icon: FileText,
      className: "text-red-600 dark:text-red-400",
      preview: true,
    },
  ],
  [
    /^(xlsx?|xlsm|ods|csv)$/,
    { icon: FileSpreadsheet, className: "text-green-700 dark:text-green-400" },
  ],
  [/^(docx?|odt|rtf|txt)$/, { icon: FileText }],
  [/^(gif|png|jpe?g|webp)$/, { icon: FileImage, preview: true }],
  [/^(heic|svg|bmp|tiff?)$/, { icon: FileImage }],
  [/^(zip|rar|7z|gz|tar)$/, { icon: FileArchive }],
  [/^(mp4|mov|avi|webm|mkv)$/, { icon: FileVideo }],
  [/^(mp3|wav|m4a|ogg|opus)$/, { icon: FileAudio }],
  [/^(eml|msg)$/, { icon: Mail }],
]

type Part = { type: "text"; value: string } | { type: "url"; value: string }

export function splitTextByUrls(text: string): Part[] {
  const parts: Part[] = []
  let last = 0
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index ?? 0
    const url = match[0].replace(TRAILING_PUNCT_RE, "")
    if (!url) continue
    if (start > last)
      parts.push({ type: "text", value: text.slice(last, start) })
    parts.push({ type: "url", value: url })
    last = start + url.length
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) })
  return parts
}

/** File name from the last path segment, or null when the URL is a page. */
export function fileNameFromUrl(url: string): string | null {
  try {
    const lastSegment = new URL(url).pathname.split("/").filter(Boolean).pop()
    const ext = lastSegment?.split(".").pop()?.toLowerCase()
    if (!lastSegment || !lastSegment.includes(".") || !ext) return null
    if (PAGE_EXT_RE.test(ext)) return null
    return decodeURIComponent(lastSegment)
  } catch {
    return null
  }
}

/**
 * Short, human label for a URL: the file name when the path ends in one
 * (`P1089….pdf`), otherwise the host (`example.com`).
 */
export function shortUrlLabel(url: string): string {
  const fileName = fileNameFromUrl(url)
  if (fileName) return fileName
  try {
    return new URL(url).hostname.replace(/^www\./, "")
  } catch {
    return url
  }
}

/** Icon (and colour) for the file the URL points at; a link icon for pages. */
export function fileKindForUrl(url: string): FileKind {
  const fileName = fileNameFromUrl(url)
  if (!fileName) return { icon: Link2 }
  const ext = fileName.split(".").pop()!.toLowerCase()
  return FILE_KINDS.find(([re]) => re.test(ext))?.[1] ?? { icon: File }
}

/**
 * Clipboard API first; when the browser denies it ("Write permission
 * denied"), fall back to the legacy `execCommand("copy")`, which only needs
 * the click that is still active.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    console.warn(
      "[AIChatUserText] clipboard API failed, trying fallback",
      error,
    )
  }
  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "")
  textarea.style.cssText = "position:fixed;top:0;left:0;opacity:0"
  document.body.appendChild(textarea)
  textarea.select()
  try {
    return document.execCommand("copy")
  } catch {
    return false
  } finally {
    textarea.remove()
  }
}

function LinkChip({ url }: { url: string }) {
  const { t } = useTranslation()
  const chipRef = useRef<HTMLSpanElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const fileName = fileNameFromUrl(url)
  const { icon: Icon, className: iconClassName, preview } = fileKindForUrl(url)

  const openPreview = useCallback(() => {
    // The surrounding AIChatLink anchor owns the in-app viewer logic.
    chipRef.current?.closest("a")?.click()
  }, [])

  const openInNewTab = useCallback(() => {
    window.open(url, "_blank", "noopener,noreferrer")
  }, [url])

  const download = useCallback(async () => {
    try {
      const response = await fetch(url)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      saveAs(await response.blob(), fileName ?? undefined)
    } catch (error) {
      console.warn("[AIChatUserText] download failed, opening instead", error)
      openInNewTab()
    }
  }, [url, fileName, openInNewTab])

  const copyLink = useCallback(async () => {
    if (await copyText(url)) {
      toast.success(t("ai.userText.linkCopied"))
    } else {
      toast.error(t("ai.userText.copyFailed"))
    }
  }, [t, url])

  return (
    <ContextMenu onOpenChange={setMenuOpen}>
      <AIChatLink href={url}>
        <Tooltip open={tooltipOpen && !menuOpen} onOpenChange={setTooltipOpen}>
          <ContextMenuTrigger asChild>
            <TooltipTrigger asChild>
              <span
                ref={chipRef}
                data-testid="ai-chat-link-chip"
                data-url={url}
                className="inline-flex max-w-full items-center gap-1 rounded bg-blue-200/60 px-1.5 align-bottom text-blue-800 underline-offset-2 hover:underline dark:bg-blue-800/60 dark:text-blue-100"
              >
                <Icon
                  className={cn("h-3.5 w-3.5 shrink-0", iconClassName)}
                  aria-hidden
                />
                <span className="truncate">{shortUrlLabel(url)}</span>
              </span>
            </TooltipTrigger>
          </ContextMenuTrigger>
          <TooltipContent className="max-w-md break-all">{url}</TooltipContent>
        </Tooltip>
      </AIChatLink>
      <ContextMenuContent>
        {preview && (
          <ContextMenuItem onSelect={openPreview}>
            <Eye />
            {t("ai.userText.openPreview")}
          </ContextMenuItem>
        )}
        <ContextMenuItem onSelect={openInNewTab}>
          <ExternalLink />
          {t("ai.userText.openInNewTab")}
        </ContextMenuItem>
        {fileName && (
          <ContextMenuItem onSelect={download}>
            <Download />
            {t("ai.userText.download")}
          </ContextMenuItem>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={copyLink}>
          <Copy />
          {t("ai.userText.copyLink")}
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  )
}

/**
 * Plain text of a user turn in the AI chat, with every URL collapsed into a
 * compact chip: a file-type icon and a short label, the full URL in a tooltip,
 * and a right-click menu (preview, new tab, download, copy link).
 */
export function AIChatUserText({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={300}>
      {splitTextByUrls(text).map((part, i) =>
        part.type === "url" ? (
          <LinkChip key={i} url={part.value} />
        ) : (
          <React.Fragment key={i}>{part.value}</React.Fragment>
        ),
      )}
    </TooltipProvider>
  )
}
